import React, { useRef, useEffect, useState, useCallback } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import {
    TerraDraw,
    TerraDrawPolygonMode,
    TerraDrawSelectMode,
    TerraDrawRectangleMode,
    TerraDrawCircleMode,
} from "terra-draw";
import { TerraDrawGoogleMapsAdapter } from "terra-draw-google-maps-adapter";
import { Button, Modal, Text } from "@/ui";
import {
    geoZoneToTerraDrawFeature,
    getDrawableFeatures,
    getGeoZoneBounds,
    terraDrawFeatureToGeoZone,
} from "utils/geoZone";
import styles from "./GoogleMapComponent.module.scss";

const patchGoogleMapsEventElement = () => {
    if (TerraDrawGoogleMapsAdapter.prototype.__eventElementPatched) {
        return;
    }

    const originalGetMapEventElement =
        TerraDrawGoogleMapsAdapter.prototype.getMapEventElement;

    TerraDrawGoogleMapsAdapter.prototype.getMapEventElement = function (
        eventType,
    ) {
        if (eventType === "keyup" || eventType === "keydown") {
            return this._map.getDiv();
        }

        const mapDiv = this._map.getDiv();
        const pane =
            mapDiv.querySelector('div[style*="z-index: 3;"]') ||
            mapDiv.querySelector('div[style*="z-index: 3"]') ||
            mapDiv.querySelector('div[style*="z-index:3"]') ||
            Array.from(mapDiv.querySelectorAll("div[style]")).find(element => {
                const zIndex = element.style.zIndex;
                return zIndex === "3" || zIndex === 3;
            });

        return (
            pane || originalGetMapEventElement.call(this, eventType) || mapDiv
        );
    };

    TerraDrawGoogleMapsAdapter.prototype.__eventElementPatched = true;
};

patchGoogleMapsEventElement();

const mapContainerStyle = {
    width: "100%",
    height: "400px",
    position: "relative",
};

const DRAWING_MODES = [
    { id: "polygon", label: "Polygon", icon: "fa-draw-polygon" },
    { id: "rectangle", label: "Rectangle", icon: "fa-vector-square" },
    { id: "circle", label: "Circle", icon: "fa-circle" },
];

const SELECT_FLAGS = {
    polygon: {
        feature: {
            draggable: true,
            coordinates: {
                midpoints: true,
                draggable: true,
                deletable: true,
            },
        },
    },
    rectangle: {
        feature: {
            draggable: true,
            coordinates: {
                midpoints: true,
                draggable: true,
                deletable: true,
            },
        },
    },
    circle: {
        feature: {
            draggable: true,
            coordinates: {
                midpoints: true,
                draggable: true,
                deletable: true,
            },
        },
    },
};

const GoogleMapComponent = ({
    onSelect,
    isOpen,
    toggleModal,
    storeKey,
    coordinates,
    selectedShapeGeoJson,
}) => {
    const mapRef = useRef(null);
    const drawRef = useRef(null);
    const projectionListenerRef = useRef(null);
    const isOpenRef = useRef(isOpen);
    const selectedShapeRef = useRef(selectedShapeGeoJson);
    const [hasShape, setHasShape] = useState(false);
    const [activeMode, setActiveMode] = useState("polygon");
    const [isDrawReady, setIsDrawReady] = useState(false);

    const center = {
        lat: coordinates?.lat ?? 36.7783,
        lng: coordinates?.lng ?? -119.4179,
    };

    const confirmLabel = geoZoneToTerraDrawFeature(selectedShapeGeoJson)
        ? "Save"
        : "Add";

    useEffect(() => {
        isOpenRef.current = isOpen;
        selectedShapeRef.current = selectedShapeGeoJson;
    }, [isOpen, selectedShapeGeoJson]);

    const syncShapeState = useCallback(() => {
        if (!drawRef.current) {
            setHasShape(false);
            return;
        }

        const drawableFeatures = getDrawableFeatures(
            drawRef.current.getSnapshot(),
        );
        setHasShape(drawableFeatures.length > 0);
    }, []);

    const fitMapToGeoZone = useCallback(
        geoZone => {
            if (!mapRef.current) {
                return;
            }

            const bounds = getGeoZoneBounds(geoZone, center);
            if (bounds) {
                mapRef.current.fitBounds(bounds, { padding: 40 });
            }
        },
        [center],
    );

    const enterSelectMode = useCallback((draw, featureId) => {
        draw.setMode("select");
        setActiveMode("select");
        if (featureId != null) {
            draw.selectFeature(featureId);
        }
    }, []);

    const setDrawingMode = useCallback(mode => {
        if (!drawRef.current?.enabled) {
            return;
        }

        drawRef.current.setMode(mode);
        setActiveMode(mode);
    }, []);

    const loadExistingShape = useCallback(
        (drawInstance = drawRef.current) => {
            if (!drawInstance) {
                return;
            }

            drawInstance.clear();

            const feature = geoZoneToTerraDrawFeature(selectedShapeRef.current);

            if (feature) {
                let results = drawInstance.addFeatures([feature]);
                let validResult = results.find(result => result.valid);

                if (!validResult?.id && feature.properties?.mode === "circle") {
                    const polygonFallback = {
                        ...feature,
                        properties: { mode: "polygon" },
                    };
                    results = drawInstance.addFeatures([polygonFallback]);
                    validResult = results.find(result => result.valid);
                }

                if (validResult?.id != null) {
                    enterSelectMode(drawInstance, validResult.id);
                    syncShapeState();
                    fitMapToGeoZone(selectedShapeRef.current);
                    return;
                }
            }

            drawInstance.setMode("polygon");
            setActiveMode("polygon");
            setHasShape(false);
        },
        [enterSelectMode, fitMapToGeoZone, syncShapeState],
    );

    const cleanupDraw = useCallback(() => {
        if (drawRef.current) {
            drawRef.current.stop();
            drawRef.current = null;
        }

        if (mapRef.current && projectionListenerRef.current) {
            window.google.maps.event.removeListener(
                projectionListenerRef.current,
            );
            projectionListenerRef.current = null;
        }

        setIsDrawReady(false);
        setHasShape(false);
        setActiveMode("polygon");
    }, []);

    const initTerraDraw = useCallback(
        map => {
            if (drawRef.current || !window.google?.maps) {
                return;
            }

            const draw = new TerraDraw({
                adapter: new TerraDrawGoogleMapsAdapter({
                    map,
                    lib: window.google.maps,
                    isolatedData: true,
                    forwardMapElementEvents: true,
                }),
                modes: [
                    new TerraDrawSelectMode({ flags: SELECT_FLAGS }),
                    new TerraDrawPolygonMode({ editable: true }),
                    new TerraDrawRectangleMode(),
                    new TerraDrawCircleMode(),
                ],
            });

            drawRef.current = draw;
            draw.start();

            draw.on("ready", () => {
                setIsDrawReady(true);
                loadExistingShape(draw);
            });

            draw.on("finish", finishedId => {
                const snapshot = draw.getSnapshot();
                const otherIds = snapshot
                    .filter(feature => feature.id !== finishedId)
                    .map(feature => feature.id);

                if (otherIds.length > 0) {
                    draw.removeFeatures(otherIds);
                }

                enterSelectMode(draw, finishedId);
                syncShapeState();
            });

            draw.on("change", syncShapeState);
        },
        [enterSelectMode, loadExistingShape, syncShapeState],
    );

    const resizeMap = useCallback(() => {
        if (!mapRef.current || !window.google?.maps) {
            return;
        }

        window.google.maps.event.trigger(mapRef.current, "resize");

        if (!mapRef.current.getBounds()) {
            mapRef.current.setCenter(center);
            mapRef.current.setZoom(10);
        }
    }, [center]);

    const onLoad = map => {
        mapRef.current = map;

        if (projectionListenerRef.current) {
            window.google.maps.event.removeListener(
                projectionListenerRef.current,
            );
        }

        const tryInit = () => {
            if (map.getProjection()) {
                initTerraDraw(map);
            }
        };

        projectionListenerRef.current = map.addListener(
            "projection_changed",
            tryInit,
        );

        tryInit();
        window.requestAnimationFrame(resizeMap);
    };

    useEffect(() => {
        if (!isOpen) {
            cleanupDraw();
            mapRef.current = null;
            return;
        }

        // Match production `onOpened`: resize once the dialog is visible.
        window.requestAnimationFrame(resizeMap);
    }, [isOpen, cleanupDraw, resizeMap]);

    const handleSelectClick = () => {
        if (!drawRef.current) {
            return;
        }

        const drawableFeatures = getDrawableFeatures(
            drawRef.current.getSnapshot(),
        );

        if (drawableFeatures.length === 0) {
            return;
        }

        const geoZone = terraDrawFeatureToGeoZone(
            drawableFeatures[drawableFeatures.length - 1],
        );

        if (!geoZone) {
            return;
        }

        onSelect(geoZone);
    };

    return (
        <Modal
            assistiveDescription="Choose a shape, then click on the map to draw a geofence."
            footer={
                <div className={styles.footerActions}>
                    <Button
                        onClick={toggleModal}
                        type="button"
                        variant="secondary"
                    >
                        Close
                    </Button>
                    <Button
                        disabled={!hasShape}
                        onClick={handleSelectClick}
                        type="button"
                        variant="primary"
                    >
                        {confirmLabel}
                    </Button>
                </div>
            }
            onOpenChange={open => {
                if (!open && isOpen) {
                    toggleModal();
                }
            }}
            open={isOpen}
            size="lg"
            title="Geofence"
        >
            <div className={styles.toolbar}>
                <div className={styles.modeGroup}>
                    {DRAWING_MODES.map(mode => (
                        <Button
                            disabled={!isDrawReady}
                            key={mode.id}
                            onClick={() => setDrawingMode(mode.id)}
                            size="sm"
                            type="button"
                            variant={
                                activeMode === mode.id ? "primary" : "secondary"
                            }
                        >
                            <i className={`fas ${mode.icon}`} />
                            {mode.label}
                        </Button>
                    ))}
                </div>
                <Button
                    disabled={!isDrawReady || !hasShape}
                    onClick={() => setDrawingMode("select")}
                    size="sm"
                    type="button"
                    variant={activeMode === "select" ? "primary" : "secondary"}
                >
                    <i className="fas fa-arrows-alt" />
                    Edit
                </Button>
            </div>
            <Text className={styles.hint} tone="secondary" variant="body-sm">
                Choose a shape, then click on the map to draw. For polygons,
                close the shape on the first point. Use Edit to adjust vertices.
            </Text>
            <div style={mapContainerStyle}>
                {isOpen ? (
                    <GoogleMap
                        center={center}
                        id="drawing-map"
                        key={storeKey}
                        mapContainerStyle={{
                            width: "100%",
                            height: "100%",
                        }}
                        onLoad={onLoad}
                        options={{
                            gestureHandling: "greedy",
                            mapTypeControl: true,
                            streetViewControl: false,
                            fullscreenControl: false,
                        }}
                        zoom={10}
                    >
                        <Marker clickable={false} position={center} />
                    </GoogleMap>
                ) : null}
            </div>
        </Modal>
    );
};

export default GoogleMapComponent;
